<script setup lang="ts">
// A wrapper that draws subtle dividers between its direct children.
// Example usage:
// <Divider>
//   <MaterialCard v-for="m in materials" :key="m.title" v-bind="m" />
// </Divider>
import { computed, Fragment, useSlots, type VNode } from 'vue';

function flatten(vnodes: VNode[] = []): VNode[] {
  // The slot content can contain Vue Fragments (e.g., when a parent uses
  // `v-for` or groups children without an extra wrapper element). A Fragment
  // is a container that renders multiple child nodes without adding an extra
  // DOM element. For our Divider we need a flat list of direct children so we
  // can wrap each one and draw a divider line between them.
  //
  // This helper walks the provided VNodes and "flattens" any Fragment it
  // encounters by pushing its children directly into the output array.
  // Non-Fragment nodes are added as-is. The result is a simple, one-level
  // array of VNodes that the template can iterate over.
  const out: VNode[] = [];
  for (const n of vnodes) {
    if (n.type === Fragment && Array.isArray(n.children)) {
      // Push children of the fragment individually so they become direct
      // siblings for rendering and spacing/divider logic.
      for (const c of n.children as VNode[]) out.push(c);
    } else {
      out.push(n);
    }
  }
  return out;
}

const slots = useSlots();
const children = computed<VNode[]>(() => flatten(slots.default?.()));
</script>

<template>
  <div class="divide-y divide-neutral-200">
    <!-- Wrap each child to apply consistent padding while the parent draws the divider lines -->
    <div v-for="(vnode, i) in children" :key="i">
      <component :is="vnode" />
    </div>
  </div>
</template>
